/**
 * Scoring configuration for DCI and IPIP assessments.
 * Maps survey question IDs to their scoring metadata (subscale/facet, domain, direction).
 */

// DCI subscale descriptions
export const DCI_SUBSCALES = {
  SCO: 'Stress Communicated by Oneself',
  SDCP: 'Supportive Dyadic Coping by Partner',
  NDCP: 'Negative Dyadic Coping by Partner',
  DDCP: 'Delegated Dyadic Coping by Partner',
  SCP: 'Stress Communication by Partner',
  SDC: 'Supportive Dyadic Coping by Oneself',
  NDCO: 'Negative Dyadic Coping by Oneself',
  DDCO: 'Delegated Dyadic Coping by Oneself',
  CDC: 'Common Dyadic Coping',
  EDC: 'Evaluation of Dyadic Coping',
}

// DCI item configuration: surveyId -> { scale, direction }
// direction: 'F' = forward (1-5), 'R' = reverse (5-1)
export const DCI_ITEMS = {
  // dci1: Stress communicated by oneself (SCO)
  dci1_1: { scale: 'SCO', dir: 'F' },
  dci1_2: { scale: 'SCO', dir: 'F' },
  dci1_3: { scale: 'SCO', dir: 'F' },
  dci1_4: { scale: 'SCO', dir: 'F' },
  // dci2: What partner does when stressed (SDCP, NDCP, DDCP)
  dci2_1:  { scale: 'SDCP', dir: 'F' },
  dci2_2:  { scale: 'SDCP', dir: 'F' },
  dci2_3:  { scale: 'NDCP', dir: 'R' },
  dci2_4:  { scale: 'SDCP', dir: 'F' },
  dci2_5:  { scale: 'SDCP', dir: 'F' },
  dci2_6:  { scale: 'NDCP', dir: 'R' },
  dci2_7:  { scale: 'NDCP', dir: 'R' },
  dci2_8:  { scale: 'DDCP', dir: 'F' },
  dci2_9:  { scale: 'SDCP', dir: 'F' },
  dci2_10: { scale: 'DDCP', dir: 'F' },
  dci2_11: { scale: 'NDCP', dir: 'R' },
  // dci3: How partner communicates stress (SCP)
  dci3_1: { scale: 'SCP', dir: 'F' },
  dci3_2: { scale: 'SCP', dir: 'F' },
  dci3_3: { scale: 'SCP', dir: 'F' },
  dci3_4: { scale: 'SCP', dir: 'F' },
  // dci4: What you do when partner stressed (SDC, NDCO, DDCO)
  dci4_1:  { scale: 'SDC', dir: 'F' },
  dci4_2:  { scale: 'SDC', dir: 'F' },
  dci4_3:  { scale: 'NDCO', dir: 'R' },
  dci4_4:  { scale: 'SDC', dir: 'F' },
  dci4_5:  { scale: 'SDC', dir: 'F' },
  dci4_6:  { scale: 'NDCO', dir: 'R' },
  dci4_7:  { scale: 'NDCO', dir: 'R' },
  dci4_8:  { scale: 'NDCO', dir: 'R' },
  dci4_9:  { scale: 'DDCO', dir: 'F' },
  dci4_10: { scale: 'SDC', dir: 'F' },
  dci4_11: { scale: 'DDCO', dir: 'F' },
  // dci5: Common dyadic coping (CDC)
  dci5_1: { scale: 'CDC', dir: 'F' },
  dci5_2: { scale: 'CDC', dir: 'F' },
  dci5_3: { scale: 'CDC', dir: 'F' },
  dci5_4: { scale: 'CDC', dir: 'F' },
  dci5_5: { scale: 'CDC', dir: 'F' },
  // dci6: Evaluation of dyadic coping (EDC) — excluded from Total DCI
  dci6_1: { scale: 'EDC', dir: 'F' },
  dci6_2: { scale: 'EDC', dir: 'F' },
}

// Items included in Total DCI (excludes EDC evaluation items)
export const DCI_TOTAL_ITEMS = Object.keys(DCI_ITEMS).filter(
  id => DCI_ITEMS[id].scale !== 'EDC'
)

// DCI total score categories (spec: ≤111 Below Avg, 112-145 Normal, ≥146 Above Avg)
// getCategory uses < comparison, so max values are exclusive upper bounds
export const DCI_CATEGORIES = [
  { max: 112, label: 'Below Average' },
  { max: 146, label: 'Normal' },
  { max: Infinity, label: 'Above Average' },
]

// IPIP domain descriptions (Big Five / OCEAN)
export const IPIP_DOMAINS = {
  N: 'Neuroticism',
  C: 'Conscientiousness',
  A: 'Agreeableness',
  E: 'Extraversion',
  O: 'Openness',
}

// IPIP facet descriptions
export const IPIP_FACETS = {
  N1: 'Anxiety', N2: 'Anger', N3: 'Depression',
  N4: 'Self-consciousness', N5: 'Immoderation', N6: 'Vulnerability',
  C1: 'Self-efficacy', C2: 'Orderliness', C3: 'Dutifulness',
  C4: 'Achievement Striving', C5: 'Self-discipline', C6: 'Cautiousness',
  A1: 'Trust', A2: 'Morality', A3: 'Altruism',
  A4: 'Co-operation', A5: 'Modesty', A6: 'Sympathy',
  E1: 'Friendliness', E2: 'Gregariousness', E3: 'Assertiveness',
  E4: 'Activity Level', E5: 'Excitement Seeking', E6: 'Cheerfulness',
  O1: 'Imagination', O2: 'Artistic Interests', O3: 'Emotionality',
  O4: 'Adventurousness', O5: 'Intellect', O6: 'Liberalism',
}

// Facets grouped by domain
export const DOMAIN_FACETS = {
  N: ['N1', 'N2', 'N3', 'N4', 'N5', 'N6'],
  C: ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'],
  A: ['A1', 'A2', 'A3', 'A4', 'A5', 'A6'],
  E: ['E1', 'E2', 'E3', 'E4', 'E5', 'E6'],
  O: ['O1', 'O2', 'O3', 'O4', 'O5', 'O6'],
}

// IPIP item configuration: surveyId -> { facet, domain, dir }
// Mapped by matching question text between formData.js and the IPIP-NEO scoring key
export const IPIP_ITEMS = {
  // pi1 (20 items)
  pi1_1:  { facet: 'E3', domain: 'E', dir: 'F' },
  pi1_2:  { facet: 'E6', domain: 'E', dir: 'F' },
  pi1_3:  { facet: 'O3', domain: 'O', dir: 'F' },
  pi1_4:  { facet: 'N6', domain: 'N', dir: 'F' },
  pi1_5:  { facet: 'A3', domain: 'A', dir: 'R' },
  pi1_6:  { facet: 'N3', domain: 'N', dir: 'F' },
  pi1_7:  { facet: 'N5', domain: 'N', dir: 'R' },
  pi1_8:  { facet: 'E2', domain: 'E', dir: 'F' },
  pi1_9:  { facet: 'A5', domain: 'A', dir: 'R' },
  pi1_10: { facet: 'N1', domain: 'N', dir: 'F' },
  pi1_11: { facet: 'E4', domain: 'E', dir: 'F' },
  pi1_12: { facet: 'C2', domain: 'C', dir: 'R' },
  pi1_13: { facet: 'E6', domain: 'E', dir: 'F' },
  pi1_14: { facet: 'O6', domain: 'O', dir: 'R' },
  pi1_15: { facet: 'A6', domain: 'A', dir: 'F' },
  pi1_16: { facet: 'C4', domain: 'C', dir: 'F' },
  pi1_17: { facet: 'A4', domain: 'A', dir: 'R' },
  pi1_18: { facet: 'E2', domain: 'E', dir: 'R' },
  pi1_19: { facet: 'C1', domain: 'C', dir: 'F' },
  pi1_20: { facet: 'A1', domain: 'A', dir: 'F' },
  // pi2 (20 items)
  pi2_1:  { facet: 'O6', domain: 'O', dir: 'R' },
  pi2_2:  { facet: 'C2', domain: 'C', dir: 'R' },
  pi2_3:  { facet: 'E5', domain: 'E', dir: 'F' },
  pi2_4:  { facet: 'E2', domain: 'E', dir: 'R' },
  pi2_5:  { facet: 'E1', domain: 'E', dir: 'R' },
  pi2_6:  { facet: 'A1', domain: 'A', dir: 'R' },
  pi2_7:  { facet: 'A6', domain: 'A', dir: 'F' },
  pi2_8:  { facet: 'O4', domain: 'O', dir: 'F' },
  pi2_9:  { facet: 'C6', domain: 'C', dir: 'R' },
  pi2_10: { facet: 'A3', domain: 'A', dir: 'F' },
  pi2_11: { facet: 'O5', domain: 'O', dir: 'F' },
  pi2_12: { facet: 'O5', domain: 'O', dir: 'R' },
  pi2_13: { facet: 'N4', domain: 'N', dir: 'R' },
  pi2_14: { facet: 'N5', domain: 'N', dir: 'R' },
  pi2_15: { facet: 'O2', domain: 'O', dir: 'F' },
  pi2_16: { facet: 'E6', domain: 'E', dir: 'F' },
  pi2_17: { facet: 'E3', domain: 'E', dir: 'F' },
  pi2_18: { facet: 'A2', domain: 'A', dir: 'R' },
  pi2_19: { facet: 'E6', domain: 'E', dir: 'F' },
  pi2_20: { facet: 'N5', domain: 'N', dir: 'R' },
  // pi3 (20 items)
  pi3_1:  { facet: 'A2', domain: 'A', dir: 'R' },
  pi3_2:  { facet: 'A6', domain: 'A', dir: 'R' },
  pi3_3:  { facet: 'A2', domain: 'A', dir: 'R' },
  pi3_4:  { facet: 'C1', domain: 'C', dir: 'F' },
  pi3_5:  { facet: 'N1', domain: 'N', dir: 'F' },
  pi3_6:  { facet: 'E4', domain: 'E', dir: 'R' },
  pi3_7:  { facet: 'C2', domain: 'C', dir: 'R' },
  pi3_8:  { facet: 'E1', domain: 'E', dir: 'F' },
  pi3_9:  { facet: 'N6', domain: 'N', dir: 'F' },
  pi3_10: { facet: 'N5', domain: 'N', dir: 'F' },
  pi3_11: { facet: 'C3', domain: 'C', dir: 'F' },
  pi3_12: { facet: 'C5', domain: 'C', dir: 'R' },
  pi3_13: { facet: 'A3', domain: 'A', dir: 'F' },
  pi3_14: { facet: 'C4', domain: 'C', dir: 'R' },
  pi3_15: { facet: 'N4', domain: 'N', dir: 'F' },
  pi3_16: { facet: 'N4', domain: 'N', dir: 'F' },
  pi3_17: { facet: 'C4', domain: 'C', dir: 'R' },
  pi3_18: { facet: 'C6', domain: 'C', dir: 'R' },
  pi3_19: { facet: 'O3', domain: 'O', dir: 'R' },
  pi3_20: { facet: 'O4', domain: 'O', dir: 'R' },
  // pi4 (20 items)
  pi4_1:  { facet: 'N2', domain: 'N', dir: 'F' },
  pi4_2:  { facet: 'A5', domain: 'A', dir: 'R' },
  pi4_3:  { facet: 'A2', domain: 'A', dir: 'R' },
  pi4_4:  { facet: 'O2', domain: 'O', dir: 'F' },
  pi4_5:  { facet: 'E1', domain: 'E', dir: 'R' },
  pi4_6:  { facet: 'C4', domain: 'C', dir: 'F' },
  pi4_7:  { facet: 'A4', domain: 'A', dir: 'R' },
  pi4_8:  { facet: 'O6', domain: 'O', dir: 'F' },
  pi4_9:  { facet: 'C6', domain: 'C', dir: 'R' },
  pi4_10: { facet: 'O2', domain: 'O', dir: 'R' },
  pi4_11: { facet: 'O3', domain: 'O', dir: 'R' },
  pi4_12: { facet: 'O2', domain: 'O', dir: 'R' },
  pi4_13: { facet: 'A1', domain: 'A', dir: 'F' },
  pi4_14: { facet: 'N2', domain: 'N', dir: 'F' },
  pi4_15: { facet: 'C3', domain: 'C', dir: 'F' },
  pi4_16: { facet: 'A5', domain: 'A', dir: 'R' },
  pi4_17: { facet: 'O5', domain: 'O', dir: 'R' },
  pi4_18: { facet: 'E1', domain: 'E', dir: 'F' },
  pi4_19: { facet: 'A1', domain: 'A', dir: 'F' },
  pi4_20: { facet: 'O3', domain: 'O', dir: 'F' },
  // pi5 (20 items)
  pi5_1:  { facet: 'O5', domain: 'O', dir: 'R' },
  pi5_2:  { facet: 'N3', domain: 'N', dir: 'F' },
  pi5_3:  { facet: 'A4', domain: 'A', dir: 'R' },
  pi5_4:  { facet: 'E4', domain: 'E', dir: 'F' },
  pi5_5:  { facet: 'N1', domain: 'N', dir: 'F' },
  pi5_6:  { facet: 'O4', domain: 'O', dir: 'R' },
  pi5_7:  { facet: 'O6', domain: 'O', dir: 'F' },
  pi5_8:  { facet: 'A3', domain: 'A', dir: 'R' },
  pi5_9:  { facet: 'C6', domain: 'C', dir: 'R' },
  pi5_10: { facet: 'A4', domain: 'A', dir: 'R' },
  pi5_11: { facet: 'E5', domain: 'E', dir: 'F' },
  pi5_12: { facet: 'N2', domain: 'N', dir: 'F' },
  pi5_13: { facet: 'O1', domain: 'O', dir: 'F' },
  pi5_14: { facet: 'C5', domain: 'C', dir: 'F' },
  pi5_15: { facet: 'E4', domain: 'E', dir: 'F' },
  pi5_16: { facet: 'C3', domain: 'C', dir: 'R' },
  pi5_17: { facet: 'O1', domain: 'O', dir: 'F' },
  pi5_18: { facet: 'N4', domain: 'N', dir: 'F' },
  pi5_19: { facet: 'N1', domain: 'N', dir: 'F' },
  pi5_20: { facet: 'O1', domain: 'O', dir: 'F' },
  // pi6 (20 items)
  pi6_1:  { facet: 'C2', domain: 'C', dir: 'F' },
  pi6_2:  { facet: 'E5', domain: 'E', dir: 'F' },
  pi6_3:  { facet: 'E5', domain: 'E', dir: 'F' },
  pi6_4:  { facet: 'C3', domain: 'C', dir: 'R' },
  pi6_5:  { facet: 'C1', domain: 'C', dir: 'F' },
  pi6_6:  { facet: 'C5', domain: 'C', dir: 'F' },
  pi6_7:  { facet: 'C5', domain: 'C', dir: 'R' },
  pi6_8:  { facet: 'E3', domain: 'E', dir: 'R' },
  pi6_9:  { facet: 'A6', domain: 'A', dir: 'R' },
  pi6_10: { facet: 'O4', domain: 'O', dir: 'R' },
  pi6_11: { facet: 'C1', domain: 'C', dir: 'F' },
  pi6_12: { facet: 'N3', domain: 'N', dir: 'R' },
  pi6_13: { facet: 'A5', domain: 'A', dir: 'R' },
  pi6_14: { facet: 'N6', domain: 'N', dir: 'F' },
  pi6_15: { facet: 'E2', domain: 'E', dir: 'F' },
  pi6_16: { facet: 'N3', domain: 'N', dir: 'F' },
  pi6_17: { facet: 'O1', domain: 'O', dir: 'F' },
  pi6_18: { facet: 'E3', domain: 'E', dir: 'F' },
  pi6_19: { facet: 'N2', domain: 'N', dir: 'R' },
  pi6_20: { facet: 'N6', domain: 'N', dir: 'R' },
}

// T-score category thresholds
export const T_SCORE_CATEGORIES = [
  { max: 35, label: 'Very Low', code: 'VL' },
  { max: 45, label: 'Moderately Low', code: 'ML' },
  { max: 55, label: 'Average', code: 'A' },
  { max: 65, label: 'Moderately High', code: 'MH' },
  { max: Infinity, label: 'Very High', code: 'VH' },
]

// MS Score category thresholds
export const MS_CATEGORIES = [
  { max: 98, label: 'Extremely Low' },
  { max: 162, label: 'Low' },
  { max: 227, label: 'Moderate' },
  { max: 292, label: 'High' },
  { max: Infinity, label: 'Extremely High' },
]

// Conversion from T-score category to 1-5 score
// N domain is inverted (high neuroticism = low converted score)
export const CONVERSION_TABLE = {
  N: { VL: 5, ML: 4, A: 3, MH: 2, VH: 1 },
  C: { VL: 1, ML: 2, A: 3, MH: 4, VH: 5 },
  A: { VL: 1, ML: 2, A: 3, MH: 4, VH: 5 },
  E: { VL: 1, ML: 2, A: 3, MH: 4, VH: 5 },
  O: { VL: 1, ML: 2, A: 3, MH: 4, VH: 5 },
}
